import { Anchor, Box, Heading, Spinner, Text, TextInput } from 'grommet'
import React, { useEffect, useRef, useState } from 'react'
import LabelText from './LabelText'
import { Constants, GlobalConfig, I18n } from 'galarm-config'
import { useDispatch, useSelector } from 'react-redux'
import { ActionCreators, StringUtils, TaskManager } from 'galarm-shared'
import TallTextArea from './TallTextArea'
import ColoredButton from './ColoredButton'
import { NavigationUtils } from 'galarm-ps-api'

const Feedback = props => {
  const dispatch = useDispatch()
  const email = useSelector(state => state.userInfo.email)

  const [feedback, setFeedback] = useState('')
  const [emailId, setEmailId] = useState(email)

  const [searchingFaqs, setSearchingFaqs] = useState(false)
  const [faqResults, setFaqResults] = useState(null)

  const isMounted = useRef(true)
  useEffect(
    () => () => {
      isMounted.current = false
    },
    []
  )

  const onChangeFeedback = event => setFeedback(event.target.value)

  const onChangeEmail = event => setEmailId(event.target.value)

  const { type, extras } = props.feedbackConfiguration

  const validateFeedback = () => {
    let validationMessages = []
    if (feedback === '') {
      validationMessages.push(
        type === Constants.TICKET_TYPES.GENERAL_FEEDBACK
          ? I18n.t('specifyFeedback')
          : I18n.t('specifyProblem')
      )
    }

    if (emailId === '') {
      validationMessages.push(I18n.t('specifyEmailId'))
    }

    if (emailId && !StringUtils.isStringAnEmailId(emailId)) {
      validationMessages.push(I18n.t('specifyValidEmailId'))
    }

    return validationMessages
  }

  const sendFeedbackCore = () => {
    dispatch(
      ActionCreators.createSupportTicket(type, emailId, feedback, extras)
    ).then(() => {
      if (emailId !== '') {
        GlobalConfig.firebaseHelpers.updateData(
          GlobalConfig.firebaseHelpers.createRef(`users/${GlobalConfig.uid}`),
          { email: emailId }
        )
      }
    })
    props.onClose()
  }

  const onSend = () => {
    const validationMessages = validateFeedback()
    if (validationMessages.length > 0) {
      NavigationUtils.showAlert(
        type === Constants.TICKET_TYPES.GENERAL_FEEDBACK
          ? I18n.t('cantSendFeedback')
          : I18n.t('cantSubmitProblem'),
        validationMessages.join('\n')
      )
      return
    }

    setSearchingFaqs(true)
    TaskManager.addHttpsCloudTask('searchFaqs', { query: feedback })
      .then(result => {
        if (!isMounted.current) return
        const faqs = result?.faqs || []
        setSearchingFaqs(false)
        if (faqs.length === 0) {
          sendFeedbackCore()
        } else {
          setFaqResults(faqs)
        }
      })
      .catch(() => {
        if (!isMounted.current) return
        setSearchingFaqs(false)
        sendFeedbackCore()
      })
  }

  const onSendAnyway = () => sendFeedbackCore()

  const onEditFeedback = () => setFaqResults(null)
  if (searchingFaqs) {
    return (
      <Box width="500px" margin="medium" align="center" gap="small">
        <Spinner />
        <Heading level={5} margin="none">
          {I18n.t('searchingFaqs')}
        </Heading>
      </Box>
    )
  }

  if (faqResults !== null) {
    const isFeedback = type === Constants.TICKET_TYPES.GENERAL_FEEDBACK
    return (
      <Box overflow="auto" width="500px" margin="medium" gap="small">
        <Heading level={4} margin="none">
          {I18n.t('faqsMayHelp')}
        </Heading>
        <Box gap="xsmall">
          {faqResults.map((item, index) => (
            <Anchor
              key={item.url || index}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer">
              {item.title}
            </Anchor>
          ))}
        </Box>
        <Box direction="row" gap="small" margin={{ top: 'small' }}>
          <ColoredButton
            label={
              isFeedback
                ? I18n.t('sendFeedbackAnyway')
                : I18n.t('submitProblemAnyway')
            }
            onClick={onSendAnyway}
            buttonTextColor="textColor"
          />
          <ColoredButton
            label={isFeedback ? I18n.t('editFeedback') : I18n.t('editProblem')}
            onClick={onEditFeedback}
            buttonTextColor="textColor"
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box overflow="auto" width="500px" margin="medium" gap="small">
      <Heading level={4} margin="none">
        {type === Constants.TICKET_TYPES.GENERAL_FEEDBACK
          ? I18n.t('feedback')
          : I18n.t('reportAProblem')}
      </Heading>
      <TallTextArea
        placeholder={
          type === Constants.TICKET_TYPES.GENERAL_FEEDBACK
            ? I18n.t('enterFeedback')
            : I18n.t('describeProblem')
        }
        height="200px"
        value={feedback}
        onChange={onChangeFeedback}
        margin={{ horizontal: '10px' }}
        resize="vertical"
      />
      <Box flex>
        <LabelText>{I18n.t('emailId')}</LabelText>
        <TextInput
          flex
          value={emailId}
          placeholder={I18n.t('enterEmailIdForFeedback')}
          onChange={onChangeEmail}
        />
      </Box>
      <Text size="small" color="dark-4">
        {I18n.t('attachScreenshotsToFeedback')}
      </Text>
      <ColoredButton
        primary
        width="100px"
        label={I18n.t('submit')}
        onClick={onSend}
        margin={{ top: 'small' }}
        buttonTextColor="white"
      />
    </Box>
  )
}

export default Feedback
